import React, { Component, CSSProperties, JSX } from "react";
import IOwTableCols from "../../../models/IOwTableCols";
import IOwTableConfig from "../../../models/IOwTableConfig";
import ISupplierModel from "../../../models/ISupplierModel";
import IUserUI from "../../../models/IUserUI";
import UIService from "../../../services/UIService";
import ChevronToggle from "../ChevronToggle/ChevronToggle";
import OwCheckbox from "../OwCheckbox/OwCheckbox";
import OwInputWrapper from "../OwInputWrapper/OwInputWrapper";
import OwLabel from "../OwLabel/OwLabel";
import OwSortHeader from "../OwSortHeader/OwSortHeader";
import * as _ from "lodash-es";
import OwTitle from "../OwTitle/OwTitle";
import { FormattedNumber } from "react-intl";
import "./OwTable.scss";

type Props = {
    /**
     * The ID to add to the table
     */
    id?: string;
    /**
     * The class to apply to the <table> tag
     */
    tableClassName?: string;
    /**
     * Should the tableClassName completely override the className on the table, otherwise it'll just append
     */
    overrideTableClassName?: boolean;
    /**
     * Should the table rows be within a tbody
     */
    encloseRowsInTbody?: boolean;
    /**
     * A style object to set on the <table> tag
     */
    style?: CSSProperties;
    /**
     * The columns interface array composing the table columns and configuration
     */
    columns: IOwTableCols[];
    /**
     * A function to determine if the Row has a detail view, and show a Chevron
     */
    rowDetailViewFn?: (row: any) => boolean; // This function will allow you to determine if the view even has a detail view
    /**
     * Allow the detail view to Always be visible to the user
     */
    detailViewAlwaysVisible?: boolean; // This property allows a detail view to always be visible
    /**
     * The main property from the Parent Row to select the Detail View data from
     */
    detailViewDataSetProp?: string; // This is the property from the main dataSet to iterate on and view the data from
    /**
     * The sub property from the 'detailViewDataSetProp' to select the Detail View data from
     */
    detailViewDataSetSubProp?: string; // If there is another property within the detail view data set to use for the rows
    /**
     * Seperate Data Set not off of the Parent Row to use for the Detail View Data Set.
     */
    detailViewDataSet?: any[];
    /**
     * The columns interface array composing the detail view table columns and configuration
     */
    detailViewColumns?: IOwTableCols[];
    /**
     * A details view title to display above the table or component
     */
    detailViewTitle?: string;
    /**
     * The details view component to use in place of the table
     */
    detailViewComponent?: any;
    /**
     * If using the built-in detail view table, show an activity indicator if fetching data
     */
    detailViewDataFetch?: boolean;
    /**
     * If using the built-in detail view table, show an activity indicator if loading the initial data
     */
    detailViewInitialData?: boolean;
    /**
     * Specify how many columns the detail view will span. Default is Total Columns - 1 (assumes an action column).
     */
    detailViewColSpan?: number;
    /**
     * The data set used in conjunction with the columns
     */
    dataSet: any[];
    /**
     * The users model object
     */
    userModel: IUserUI;
    /**
     * The suppliers model object
     */
    supplierModel: ISupplierModel;
    /**
     * Is data currently being fetched, to show an activity indicator
     */
    dataFetch: boolean;
    /**
     * Is the initial data set currently being fetched.
     */
    initialData: boolean;
    /**
     * A table configuration object allowing some different stylings, and usability options
     */
    tableConfig?: IOwTableConfig;
    /**
     * A function called when the chevron is toggled.
     */
    chevronToggleCallbackFn?: (rowIndex: number, detailViewOpen: boolean) => void;
    /**
     * If the table has a detail view or not.
     */
    isDetailViewTable?: boolean;
    /**
     * Used in conjuction with the built-in detail view, will send the parent row index
     */
    parentRowIndex?: number;
    /**
     * Specify a custom row name, useful for capturing the right row, with many tables on a view
     */
    rowIdName?: string;
    /**
     * The current sort column
     */
    currentSortCol?: string;
    /**
     * Maintain the height of the table between data refreshes / loadings
     */
    maintainTableHeight?: boolean; // maintain the height of the table between data refresh
    /**
     * Check all rows of data
     */
    checkAllFn?: (checkAll: boolean) => void;
    /**
     * If we need a mixed mode detail view scenario, some rows with the built in table or some with the component
     */
    detailViewRowOverride?: (row: any, parentRowIndex?: number) => boolean;
    /**
     * For use with the Select All to show the total rows selected.
     */
    totalRows?: number;
    /**
     * The classname to add on the wrapping table div.
     */
    wrappingClassName?: string;
    noDefaultWrappingClassName?: boolean;
    /**
     * Show initial detail views as visible
     */
    initialDetailViewVisible?: boolean;
    rowKeyProp?: string;
    /**
     * Add a specific class to all table rows
     */
    tableRowClassName?: string;
};

type State = {
    rowDetailViewOpen: { [key: number]: boolean };
    isCheckAllChecked: boolean;
    selectAll: boolean;
};

class OwTable extends Component<Props, State> {
    rowRefs: { [key: number]: HTMLTableRowElement | null } = {};
    tableRef: HTMLTableElement | null = null;

    readonly state: State = {
        rowDetailViewOpen: {},
        isCheckAllChecked: false,
        selectAll: false,
    };

    componentDidMount(): void {
        const rowDetailViewOpen = this.state.rowDetailViewOpen;

        let index = 0;

        if (this.props.dataSet.length) {
            for (const data of this.props.dataSet) {
                // console.log("isInitialDetailViewVisible", this.props.initialDetailViewVisible);
                rowDetailViewOpen[index] = this.props.initialDetailViewVisible || false;
                index++;
            }
        }

        this.setState({
            rowDetailViewOpen,
        });
    }

    componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>, snapshot?: any): void {
        // Check if dataFetch has changed from true to false
        if (prevProps.dataFetch === true && this.props.dataFetch === false) {
            // Reset all rowDetailViewOpen to false
            const rowDetailViewOpen: { [key: number]: boolean } = {};

            // Initialize all rows to false
            if (this.props.dataSet.length) {
                for (let i = 0; i < this.props.dataSet.length; i++) {
                    rowDetailViewOpen[i] = false;
                }
            }

            // Update the state
            this.setState({ rowDetailViewOpen });

            // If there's a callback function, notify about all rows being closed
            if (this.props.chevronToggleCallbackFn) {
                for (let i = 0; i < this.props.dataSet.length; i++) {
                    this.props.chevronToggleCallbackFn(i, false);
                }
            }
        }
    }

    checkAll = () => {
        this.setState(
            {
                isCheckAllChecked: !this.state.isCheckAllChecked,
            },
            () => {
                if (this.props.checkAllFn) {
                    this.props.checkAllFn(this.state.isCheckAllChecked);
                }
            }
        );
    };

    selectAllRows = () => {
        this.setState(
            {
                selectAll: !this.state.selectAll,
            },
            () => {
                for (const col of this.props.columns) {
                    if (col.hasSelectAll && col.selectAllFn) {
                        col.selectAllFn(this.state.selectAll);
                    }
                }
            }
        );
    };

    getTableHeaderRow = () => {
        return this.props.columns.map((col: IOwTableCols, colIndex: number) => {
            if (col.notVisible) {
                return null;
            }

            if (!_.isNil(col.isChecked) && !col.isChecked()) {
                return null;
            }

            let className = col.className || "";

            if (this.props.tableConfig?.alignHeadersMiddle) {
                className += " align-middle";
            }

            if (!col.isActionsCol) {
                let title = col.skipTitleTranslate ? (
                    col.title
                ) : (
                    <OwLabel
                        supplierModel={this.props.supplierModel}
                        isSupplierVendorOverride={col.isVendorOverride}
                        noBottomMargin={true}
                        title={col.title}
                        rowHasSort={this.props.tableConfig?.tableHasSort}
                    />
                );

                let sortableElem: null | any = null;

                if (col.isSortable && (col.sortOrder || col.sortOrderFn) && col.sortFn && this.props.currentSortCol) {
                    if (col.sortOrder) {
                        sortableElem = (
                            <OwSortHeader
                                title={col.title}
                                currentSortCol={this.props.currentSortCol}
                                currentSortOrder={col.sortOrder}
                                sortFn={col.sortFn}
                                property={col.valueProp}
                                supplierModel={this.props.supplierModel}
                                isSupplierVendorOverride={col.isVendorOverride}
                            />
                        );
                    } else if (col.sortOrderFn) {
                        sortableElem = (
                            <OwSortHeader
                                title={col.title}
                                currentSortCol={this.props.currentSortCol}
                                currentSortOrder={col.sortOrderFn()}
                                sortFn={col.sortFn}
                                property={col.valueProp}
                                supplierModel={this.props.supplierModel}
                                isSupplierVendorOverride={col.isVendorOverride}
                            />
                        );
                    }
                }

                let headerActionIconClass = "";

                if (_.isString(col.headerActionIcon)) {
                    headerActionIconClass = col.headerActionIcon;
                }

                if (_.isFunction(col.headerActionIcon)) {
                    headerActionIconClass = col.headerActionIcon();
                }

                if (col.headerActionFn) {
                    headerActionIconClass = `${headerActionIconClass} pointer`;
                }

                if (col.titleRenderFn) {
                    title = col.titleRenderFn(col);
                }

                return (
                    <th key={colIndex} scope={"col"} className={className}>
                        {sortableElem ? sortableElem : title}
                        {col.headerActionIcon ? (
                            <i
                                className={headerActionIconClass}
                                onClick={(e: any) => {
                                    if (col.headerActionFn) {
                                        col.headerActionFn(col.title);
                                    }
                                }}></i>
                        ) : null}
                    </th>
                );
            } else {
                let innerData: any = null;

                if (col.hasCheckAll) {
                    innerData = (
                        <div>
                            <span className={"state-circle me-4"}></span>
                            <OwCheckbox
                                className={"pointer"}
                                checked={this.state.isCheckAllChecked || this.state.selectAll}
                                truthyVal={true}
                                falsyVal={false}
                                name={"checkAll"}
                                fn={this.checkAll}
                            />
                        </div>
                    );
                }

                if (col.hasSelectAll) {
                    const selectAllCount = ` ${this.props.totalRows || 0}`;

                    innerData = (
                        <div className={"d-flex"}>
                            {innerData}
                            <div className={"ms-2"}>
                                <span className={"text-underline pointer"} onClick={this.selectAllRows}>
                                    {this.state.selectAll ? (
                                        <OwLabel title={"unselect_all"} className={"pointer"} postText={selectAllCount} />
                                    ) : (
                                        <OwLabel title={"select_all"} className={"pointer"} postText={selectAllCount} />
                                    )}
                                </span>
                            </div>
                        </div>
                    );
                }

                if (col.titleRenderFn) {
                    innerData = col.titleRenderFn(col);
                }

                return (
                    <th key={colIndex} className={className} scope={"col"}>
                        {innerData}
                    </th>
                );
            }
        });
    };

    toggleChevron = (rowIndex: number) => {
        const rowDetailViewOpen = this.state.rowDetailViewOpen;

        rowDetailViewOpen[rowIndex] = !rowDetailViewOpen[rowIndex];

        this.setState(
            {
                rowDetailViewOpen,
            },
            () => {
                if (this.props.chevronToggleCallbackFn) {
                    const detailViewOpen = rowDetailViewOpen[rowIndex];

                    this.props.chevronToggleCallbackFn(rowIndex, detailViewOpen);
                }
            }
        );
    };

    getDetailViewComponent = (row: any, index: number) => {
        if (_.isFunction(this.props.detailViewComponent)) {
            return this.props.detailViewComponent(row, index);
        } else {
            return this.props.detailViewComponent;
        }
    };

    compileRows = (): any => {
        // For detail views, we'll return pairs of rows instead of nested tbodies
        const allRows: JSX.Element[] = [];

        this.props.dataSet.forEach((row: any, rowIndex: number) => {
            const rowData = this.props.columns.map((col, colIndex) => {
                if (col.notVisible) {
                    return null;
                }

                if (!_.isNil(col.isChecked) && !col.isChecked()) {
                    return null;
                }

                const scope = colIndex === 1 ? "row" : "";

                if (col.isActionsCol) {
                    let data: any;

                    if (col.renderFn) {
                        data = col.renderFn(row, rowIndex, this.props.isDetailViewTable, this.props.parentRowIndex);
                    }

                    let showChevron = false;

                    if (col.hasChevron) {
                        if (col.showChevronFn) {
                            showChevron = col.showChevronFn(row);
                        } else {
                            showChevron = true;
                        }
                    }

                    return (
                        <td key={colIndex} scope={scope} className={col.className} data-testid={`testid-row-${rowIndex}-${colIndex}`}>
                            <div className={col.hasChevron ? "d-flex" : ""}>
                                {col.hasChevron ? (
                                    <ChevronToggle
                                        className={"me-2 " + (showChevron ? "" : "d-none d-print-none")}
                                        toggleFn={() => {
                                            this.toggleChevron(rowIndex);
                                        }}
                                        isVisible={this.state.rowDetailViewOpen[rowIndex]}
                                        showChevron={showChevron}
                                    />
                                ) : null}
                                {data}
                            </div>
                        </td>
                    );
                } else {
                    let data: any;

                    let baseObject = row;
                    let hasBaseObject = false;
                    let baseObjectFound = false;

                    if (col.baseObject) {
                        hasBaseObject = true;
                    }

                    if (col.baseObject && row[col.baseObject]) {
                        baseObject = row[col.baseObject];
                        baseObjectFound = true;
                    }

                    if (baseObject[col.valueProp]) {
                        data = baseObject[col.valueProp];
                    }

                    if (col.valueSubProp && !col.isArray) {
                        if (data && data[col.valueSubProp]) {
                            data = data[col.valueSubProp];
                        } else {
                            data = "";
                        }
                    } else {
                        if (col.arrayIndex && col.valueSubProp) {
                            if (data[col.arrayIndex] && data[col.arrayIndex][col.valueSubProp]) {
                                data = data[col.arrayIndex][col.valueSubProp];
                            }
                        }
                    }

                    if (hasBaseObject && !baseObjectFound) {
                        data = "";
                    }

                    if (col.renderFn) {
                        if (col.renderFnEmitsRow) {
                            data = col.renderFn(row, rowIndex, this.props.isDetailViewTable, this.props.parentRowIndex);
                        } else {
                            data = col.renderFn(data, rowIndex, this.props.isDetailViewTable, this.props.parentRowIndex);
                        }
                    }

                    if (col.isNumberFormat) {
                        data = <FormattedNumber value={data} />;
                    }

                    if (col.translateValue) {
                        data = <OwLabel noBottomMargin={true} title={data.toLowerCase()} />;
                    }

                    if (!col.isTextInput) {
                        return (
                            <td key={colIndex} className={col.className} scope={scope} data-testid={`testid-row-${rowIndex}-${colIndex}`}>
                                {data}
                            </td>
                        );
                    } else {
                        return (
                            <td key={colIndex} className={col.className} scope={scope} data-testid={`testid-row-${rowIndex}-${colIndex}`}>
                                <OwInputWrapper
                                    fn={(e: any) => {
                                        if (col.inputFn) {
                                            col.inputFn(row, e);
                                        }
                                    }}
                                    name={col.title}
                                    value={data}
                                />
                            </td>
                        );
                    }
                }
            });

            let buildDetailView = this.props.tableConfig?.hasDetailView;

            if (this.props.rowDetailViewFn) {
                buildDetailView = this.props.rowDetailViewFn(row);
            }

            let rowClassName = this.props.tableRowClassName || "";
            if (this.props.tableConfig?.tableStriped) {
                rowClassName += rowIndex % 2 ? " row-even" : " row-odd";
            }

            const key = this.props.rowKeyProp ? row[this.props.rowKeyProp] : rowIndex;
            const rowName = this.props.rowIdName ? this.props.rowIdName : "row";
            let idVal = `${rowName}-${rowIndex}`;

            if (!_.isNil(this.props.parentRowIndex)) {
                idVal = `${rowName}-${this.props.parentRowIndex}-${rowIndex}`;
            }

            // Add the main row
            allRows.push(
                <tr key={`${key}-main`} id={idVal} className={rowClassName}>
                    {rowData}
                </tr>
            );

            // If it has a detail view and it's open, add the detail row
            if (buildDetailView) {
                let detailViewDataSet: any[] = [];

                if (this.props.detailViewDataSetProp) {
                    let rowDetailViewDataSet = row[this.props.detailViewDataSetProp];

                    if (this.props.detailViewDataSetSubProp && !_.isArray(rowDetailViewDataSet)) {
                        rowDetailViewDataSet = rowDetailViewDataSet[this.props.detailViewDataSetSubProp];
                        // rowDetailViewDataSet = rowDetailViewDataSet.map((dataItem: any) => {
                        //     // @ts-ignore
                        //     return dataItem[this.props.detailViewDataSetSubProp];
                        // });
                    }

                    detailViewDataSet = rowDetailViewDataSet;
                }

                if (this.props.rowDetailViewFn && this.props.rowDetailViewFn(row)) {
                    detailViewDataSet = row;
                }

                let rowDetailViewOpen = this.state.rowDetailViewOpen[rowIndex];

                if (this.props.detailViewAlwaysVisible) {
                    rowDetailViewOpen = true;
                }

                const dummyTd: any = null;
                const detailViewColSpan = this.props.columns.length;

                // Your existing code for handling dummy cells and colspan
                // ... (keep all the code for calculating dummyTd and detailViewColSpan)

                let showDetailViewComponent = false;

                if (this.props.detailViewRowOverride) {
                    showDetailViewComponent = this.props.detailViewRowOverride(row, this.props.parentRowIndex);
                } else {
                    if (this.props.detailViewComponent) {
                        showDetailViewComponent = true;
                    }
                }

                // If the detail view is open, add it as a separate row
                if (rowDetailViewOpen) {
                    allRows.push(
                        <tr key={`${key}-detail`}>
                            {dummyTd}
                            <td colSpan={detailViewColSpan}>
                                <div>
                                    {this.props.detailViewTitle ? <OwTitle title={this.props.detailViewTitle} tag={"h3"} /> : null}
                                    {showDetailViewComponent && this.props.detailViewComponent ? (
                                        this.getDetailViewComponent(row, rowIndex)
                                    ) : (
                                        <OwTable
                                            style={{ backgroundColor: "transparent" }}
                                            columns={this.props.detailViewColumns || []}
                                            dataSet={detailViewDataSet || []}
                                            userModel={this.props.userModel}
                                            supplierModel={this.props.supplierModel}
                                            dataFetch={this.props.detailViewDataFetch || false}
                                            initialData={this.props.detailViewInitialData || false}
                                            isDetailViewTable={true}
                                            parentRowIndex={rowIndex}
                                        />
                                    )}
                                </div>
                            </td>
                        </tr>
                    );
                }
            }
        });

        return allRows;
    };

    addStaticTableHeight = () => {
        if (this.tableRef && this.props.maintainTableHeight) {
            const height = this.tableRef.offsetHeight;
            // console.log("addStaticTableHeight", height);

            this.tableRef.style.height = `${height}px`;
        }
    };

    removeStaticTableHeight = () => {
        if (this.tableRef && this.props.maintainTableHeight) {
            // let height = this.tableRef.offsetHeight;
            // console.log("removeStaticTableHeight");

            this.tableRef.style.height = ``;
        }
    };

    render() {
        const headerRow = this.getTableHeaderRow();
        let tableContent;

        if (this.props.initialData && this.props.dataFetch) {
            tableContent = (
                <tr>
                    <td className={"text-center align-middle"} colSpan={this.props.columns.length}>
                        <OwLabel title={"retrieving_initial_data"} noBottomMargin={true} leftIconClass={"fas fa-circle-notch fa-spin"} />
                    </td>
                </tr>
            );
        } else if (!this.props.initialData && this.props.dataFetch) {
            this.addStaticTableHeight();
            tableContent = (
                <tr>
                    <td className={"text-center align-middle"} colSpan={this.props.columns.length}>
                        <OwLabel title={"retrieving_data"} noBottomMargin={true} leftIconClass={"fas fa-circle-notch fa-spin"} />
                    </td>
                </tr>
            );
        } else if (this.props.dataSet.length === 0) {
            this.removeStaticTableHeight();
            tableContent = (
                <tr>
                    <td className={"text-center"} colSpan={this.props.columns.length}>
                        <OwLabel title={"no_rows_to_show"} noBottomMargin={true} />
                    </td>
                </tr>
            );
        } else {
            this.removeStaticTableHeight();
            tableContent = this.compileRows();
        }

        let className = " table ";

        if (!UIService.isMainViewLight()) {
            className += " table-dark ";
        }

        if (this.props.tableConfig?.tableHover) {
            className += " table-hover ";
        }

        if (this.props.tableConfig?.tableStriped) {
            className += " table-striped ";
        }

        if (this.props.tableConfig?.tableSmall) {
            className += " table-sm ";
        }

        if (this.props.tableClassName) {
            if (this.props.overrideTableClassName) {
                className = this.props.tableClassName;
            } else {
                className += this.props.tableClassName;
            }
        }

        let wrappingClassName = "OwTable ";

        if (this.props.noDefaultWrappingClassName) {
            wrappingClassName = "";
        }

        if (this.props.wrappingClassName) {
            wrappingClassName += this.props.wrappingClassName;
        }

        if (this.props.tableConfig?.stickyHeader && this.props.tableConfig?.stickyHeaderMaxHeight) {
            wrappingClassName += " sticky-header-wrapper";
        }

        // check if development is on and there are no columns
        if (!this.props.columns.length && process.env.NODE_ENV !== "production") {
            return "Your missing table columns...";
        }

        const wrapperStyle: CSSProperties = {};
        if (this.props.tableConfig?.stickyHeader && this.props.tableConfig?.stickyHeaderMaxHeight) {
            wrapperStyle.maxHeight = this.props.tableConfig.stickyHeaderMaxHeight;
            wrapperStyle.minHeight = this.props.tableConfig.stickyHeaderMaxHeight;
        }

        return (
            <div className={wrappingClassName} id={this.props.id} style={wrapperStyle}>
                <table
                    ref={(ref) => {
                        this.tableRef = ref;
                    }}
                    className={className}
                    style={this.props.style}>
                    <thead>
                        <tr>{headerRow}</tr>
                    </thead>
                    <tbody>{tableContent}</tbody>
                </table>
            </div>
        );
    }
}

export default OwTable;
